<template>
  <v-dialog
    v-model="dialog"
    max-width="500px"
    persistent
  >
    <v-card>
      <v-card-title>
        <v-icon left>mdi-plus-circle</v-icon>
        {{ $t('price_list.dialog.title') || 'Add New Price List' }}
      </v-card-title>

      <v-divider />

      <v-form ref="form" v-model="valid" lazy-validation>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="form.name"
                  :label="$t('price_list.dialog.name') || 'Price List Name'"
                  :rules="nameRules"
                  outlined
                  dense
                  prepend-inner-icon="mdi-format-title"
                  :hint="$t('price_list.dialog.name_hint') || 'Enter a descriptive name for this price list'"
                  persistent-hint
                  autofocus
                />
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="form.description"
                  :label="$t('price_list.dialog.description') || 'Description'"
                  :rules="descriptionRules"
                  outlined
                  dense
                  rows="3"
                  prepend-inner-icon="mdi-text"
                  :hint="$t('price_list.dialog.description_hint') || 'Provide details about when and how this price list should be used'"
                  persistent-hint
                />
              </v-col>

              <v-col cols="12">
                <v-alert
                  dense
                  text
                  type="info"
                  class="mb-0"
                >
                  <div class="text-caption">
                    {{ $t('price_list.dialog.position_info') || 'This price list will be inserted at position' }}
                    <strong>{{ position + 1 }}</strong>
                    {{ $t('price_list.dialog.position_info2') || 'in the list hierarchy.' }}
                  </div>
                </v-alert>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-form>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          @click="close"
          :disabled="loading"
        >
          <v-icon left>mdi-close</v-icon>
          {{ $t('button.cancel') || 'Cancel' }}
        </v-btn>
        <v-btn
          color="primary"
          @click="save"
          :loading="loading"
          :disabled="!valid || loading"
        >
          <v-icon left>mdi-check</v-icon>
          {{ $t('button.create') || 'Create' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'AddPriceList',
  props: {
    display: {
      type: Boolean,
      default: false
    },
    position: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      dialog: false,
      valid: true,
      loading: false,
      form: {
        name: '',
        description: ''
      },
      nameRules: [
        v => !!v || this.$t('validation.required') || 'Name is required',
        v => (v && v.length >= 3) || this.$t('validation.min_length') || 'Name must be at least 3 characters'
      ],
      descriptionRules: [
        v => !!v || this.$t('validation.required') || 'Description is required',
        v => (v && v.length >= 10) || this.$t('validation.min_length') || 'Description must be at least 10 characters'
      ]
    }
  },
  watch: {
    display (val) {
      this.dialog = val
      if (val) {
        // Reset form when dialog opens
        this.resetForm()
      }
    },
    dialog (val) {
      if (!val) {
        this.$emit('close')
      }
    }
  },
  methods: {
    close () {
      this.dialog = false
    },
    resetForm () {
      this.form = {
        name: '',
        description: ''
      }
      this.$refs.form && this.$refs.form.resetValidation()
    },
    async save () {
      if (!this.$refs.form.validate()) {
        return
      }

      this.loading = true
      try {
        await this.withLoading(async () => {
          const data = await this.$configService.addPriceList({
            ...this.form,
            position: this.position
          })
          this.$emit('created', data.data || data)
          this.showSuccess(this.$t('price_list.message.created') || 'Price list created successfully')
        }, 'loading')
      } catch (error) {
        // Error is already handled by the global interceptor
        console.log('Component caught error in createPriceList:', error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
/* Ensure dialog content is properly spaced */
.v-card__text {
  padding-top: 16px !important;
}
</style>
